/**
 * Inbox mint memory — decline fingerprints, recently completed routes,
 * scheduled-board awareness, and distance mix.
 *
 * Decline/delete still tombstone by RFQ id (0.3.71). Fetch remints new ids,
 * so origin→dest fingerprints keep the same charter from coming back.
 * Trip Log / completed tickets are the source of “recently flown.”
 * Scheduled board tickets are soft-avoided. Do not invent history.
 */
import { normalizeIcao } from "./airportDoctrine";
import { haversineNm } from "./geo";
import { resolveCoords } from "./airports";
import type {
  CharterRequest,
  FlightLogEntry,
  InboxRouteMemoryEntry,
  InboxRouteMemoryKind,
  Mission,
  SceneryAirport,
} from "./types";

export type { InboxRouteMemoryEntry, InboxRouteMemoryKind };

export const DEFAULT_RECENT_ROUTE_COOLDOWN_DAYS = 2;
export const RECENT_ROUTE_COOLDOWN_DAYS_MIN = 1;
export const RECENT_ROUTE_COOLDOWN_DAYS_MAX = 7;
export const INBOX_ROUTE_MEMORY_MAX = 80;

/**
 * Destinations / scan this large means remint + dest-wide cool-down are
 * hints — they must not wipe a usable Grok set or force Classic.
 */
export const LARGE_ALLOWLIST_ICAO_FLOOR = 20;

/** Useful Hawker 800XP charter bands from a CYPD-type home. */
export const CHARTER_DISTANCE_SHORT_NM = 200;
export const CHARTER_DISTANCE_MEDIUM_NM = 700;
export const CHARTER_DISTANCE_LONG_NM = 1200;

export type RecentRouteSettings = {
  /** Default ON — deprioritize recently completed origin↔dest / dest. */
  deprioritizeRecentRoutes: boolean;
  /** Cool-down window in days. Default 2 (~48h). */
  recentRouteCooldownDays: number;
};

export type RouteLeg = {
  originIcao: string;
  destIcao: string;
  atUtc?: string;
  departureIso?: string;
};

export type MintAvoidance = {
  settings: RecentRouteSettings;
  hardPairs: Set<string>;
  hardDests: Set<string>;
  softPairs: Set<string>;
  softDests: Set<string>;
  recentLongDests: Set<string>;
  recentRegions: Set<string>;
  recentNms: number[];
};

export type DestRegion =
  | "maritimes"
  | "quebec"
  | "ontario"
  | "prairies"
  | "west"
  | "us_ne"
  | "us_mid"
  | "us_fl"
  | "us_other"
  | "caribbean"
  | "mexico"
  | "other";

const REGION_ICAOS: Record<Exclude<DestRegion, "us_other" | "other">, readonly string[]> = {
  maritimes: ["CYPD", "CYHZ", "CYQY", "CYYT", "CYQM", "CYYG", "CYGP", "CYQX", "CYDF", "CYSJ", "CYFC"],
  quebec: ["CYHU", "CYUL", "CYQB", "CYMX", "CYOW"],
  ontario: ["CYKZ", "CYTZ", "CYHM", "CYKF", "CYOO", "CYYZ", "CYYZ"],
  prairies: ["CYWG", "CYXE", "CYQR", "CYYC", "CYEG"],
  west: ["CYVR", "CYYJ", "CYLW", "CYYC"],
  us_ne: [
    "KTEB", "KHPN", "KBED", "KBOS", "KJFK", "KEWR", "KSWF", "KORH", "KMMU", "KFRG",
    "KMHT", "KBGR", "KACK", "KBTV", "KPVD",
  ],
  us_mid: ["KIAD", "KPHL", "KBWI", "KCLT", "KDCA", "KHEF"],
  us_fl: ["KPBI", "KMIA", "KFLL", "KOPF", "KFXE", "KAPF", "KBCT", "KMCO"],
  caribbean: ["MYNN", "TJSJ", "TXKF", "TBPB", "TNCM", "MDPC", "MKJS", "MWCR", "TNCA", "TIST"],
  mexico: ["MMUN", "MMPR", "MMSD", "MMTO", "MMMX", "MMCZ", "MMMY"],
};

const ICAO_REGION = new Map<string, DestRegion>();
for (const [region, icaos] of Object.entries(REGION_ICAOS) as Array<[DestRegion, readonly string[]]>) {
  for (const icao of icaos) ICAO_REGION.set(icao, region);
}

export function destRegionOf(icao?: string | null): DestRegion {
  const code = normalizeIcao(icao);
  if (!code) return "other";
  const hit = ICAO_REGION.get(code);
  if (hit) return hit;
  if (code.startsWith("CY") || code.startsWith("CZ")) return "other";
  if (code.startsWith("K")) return "us_other";
  if (code.startsWith("MY") || code.startsWith("TJ") || code.startsWith("TN") || code.startsWith("MB")) {
    return "caribbean";
  }
  if (code.startsWith("MM")) return "mexico";
  return "other";
}

export function hydrateRecentRouteSettings(raw?: Partial<RecentRouteSettings> | null): RecentRouteSettings {
  const daysRaw = raw?.recentRouteCooldownDays;
  const days = typeof daysRaw === "number" ? daysRaw : Number(daysRaw);
  return {
    deprioritizeRecentRoutes: raw?.deprioritizeRecentRoutes !== false,
    recentRouteCooldownDays: Number.isFinite(days)
      ? Math.min(RECENT_ROUTE_COOLDOWN_DAYS_MAX, Math.max(RECENT_ROUTE_COOLDOWN_DAYS_MIN, Math.round(days)))
      : DEFAULT_RECENT_ROUTE_COOLDOWN_DAYS,
  };
}

export function directionalFingerprint(origin?: string | null, dest?: string | null): string {
  return `${normalizeIcao(origin)}>${normalizeIcao(dest)}`;
}

export function pairFingerprint(origin?: string | null, dest?: string | null): string {
  const a = normalizeIcao(origin);
  const b = normalizeIcao(dest);
  if (!a || !b) return "";
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

export function hydrateInboxRouteMemory(raw: unknown, now = new Date()): InboxRouteMemoryEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: InboxRouteMemoryEntry[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const rec = row as Partial<InboxRouteMemoryEntry>;
    const originIcao = normalizeIcao(rec.originIcao);
    const destIcao = normalizeIcao(rec.destIcao);
    const atUtc = typeof rec.atUtc === "string" ? rec.atUtc : "";
    const kind = rec.kind === "deleted" ? "deleted" : rec.kind === "declined" ? "declined" : "";
    if (!originIcao || !destIcao || !atUtc || !kind) continue;
    const t = Date.parse(atUtc);
    if (!Number.isFinite(t)) continue;
    const key = `${kind}:${directionalFingerprint(originIcao, destIcao)}:${atUtc}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      originIcao,
      destIcao,
      atUtc: new Date(t).toISOString(),
      kind,
      departureIso: typeof rec.departureIso === "string" && rec.departureIso ? rec.departureIso : undefined,
    });
  }
  return pruneInboxRouteMemory(out, now);
}

export function pruneInboxRouteMemory(
  rows: InboxRouteMemoryEntry[],
  now = new Date(),
  windowMs = RECENT_ROUTE_COOLDOWN_DAYS_MAX * 24 * 60 * 60_000,
): InboxRouteMemoryEntry[] {
  const cutoff = now.getTime() - windowMs;
  const kept = rows
    .filter((row) => Date.parse(row.atUtc) >= cutoff)
    .sort((a, b) => Date.parse(b.atUtc) - Date.parse(a.atUtc));
  return kept.slice(0, INBOX_ROUTE_MEMORY_MAX);
}

export function stampInboxRouteMemory(
  existing: InboxRouteMemoryEntry[] | undefined,
  request: Pick<CharterRequest, "originIcao" | "destIcao" | "departureIso">,
  kind: InboxRouteMemoryKind,
  now = new Date(),
): InboxRouteMemoryEntry[] {
  const originIcao = normalizeIcao(request.originIcao);
  const destIcao = normalizeIcao(request.destIcao);
  if (!originIcao || !destIcao) return hydrateInboxRouteMemory(existing, now);
  return pruneInboxRouteMemory(
    [
      {
        originIcao,
        destIcao,
        atUtc: now.toISOString(),
        kind,
        departureIso: request.departureIso,
      },
      ...(existing ?? []),
    ],
    now,
  );
}

function withinWindow(atUtc: string | undefined, now: Date, windowMs: number): boolean {
  if (!atUtc) return false;
  const t = Date.parse(atUtc);
  return Number.isFinite(t) && now.getTime() - t >= 0 && now.getTime() - t <= windowMs;
}

function completedAtUtc(row: Pick<FlightLogEntry, "status" | "arrivedAtUtc" | "updatedAtUtc" | "createdAtUtc">): string | undefined {
  if (row.status !== "complete") return undefined;
  return row.arrivedAtUtc || row.updatedAtUtc || row.createdAtUtc;
}

function missionCompletedAt(row: Pick<Mission, "status" | "completedAt" | "arrivedAtUtc">): string | undefined {
  if (row.status !== "completed") return undefined;
  return row.completedAt || row.arrivedAtUtc;
}

export function recentCompletedLegs(input: {
  flightLogs?: ReadonlyArray<Pick<FlightLogEntry, "originIcao" | "destIcao" | "status" | "arrivedAtUtc" | "updatedAtUtc" | "createdAtUtc" | "flightType">>;
  missions?: ReadonlyArray<Pick<Mission, "originIcao" | "destIcao" | "status" | "completedAt" | "arrivedAtUtc" | "flightType">>;
  now?: Date;
  windowMs: number;
}): RouteLeg[] {
  const now = input.now ?? new Date();
  const out: RouteLeg[] = [];
  const seen = new Set<string>();
  const push = (origin: string, dest: string, atUtc?: string) => {
    const o = normalizeIcao(origin);
    const d = normalizeIcao(dest);
    if (!o || !d || o === d) return;
    if (!withinWindow(atUtc, now, input.windowMs)) return;
    const key = `${directionalFingerprint(o, d)}:${atUtc ?? ""}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ originIcao: o, destIcao: d, atUtc });
  };
  for (const row of input.flightLogs ?? []) {
    push(row.originIcao, row.destIcao, completedAtUtc(row));
  }
  for (const row of input.missions ?? []) {
    push(row.originIcao, row.destIcao, missionCompletedAt(row));
  }
  return out;
}

export function scheduledLegs(
  missions?: ReadonlyArray<Pick<Mission, "originIcao" | "destIcao" | "status" | "departureIso" | "flightType">>,
): RouteLeg[] {
  const out: RouteLeg[] = [];
  for (const row of missions ?? []) {
    if (row.status !== "pending" && row.status !== "in-progress") continue;
    const o = normalizeIcao(row.originIcao);
    const d = normalizeIcao(row.destIcao);
    if (!o || !d || o === d) continue;
    out.push({ originIcao: o, destIcao: d, departureIso: row.departureIso });
  }
  return out;
}

export function hopNm(
  originIcao: string,
  destIcao: string,
  scenery: SceneryAirport[] = [],
): number | null {
  const oc = resolveCoords(originIcao, scenery);
  const dc = resolveCoords(destIcao, scenery);
  if (!oc || !dc) return null;
  return haversineNm(oc.lat, oc.lon, dc.lat, dc.lon);
}

export function distanceBand(nm: number | null | undefined): "short" | "medium" | "long" | "extreme" | null {
  if (nm == null || !Number.isFinite(nm)) return null;
  if (nm < CHARTER_DISTANCE_SHORT_NM) return "short";
  if (nm < CHARTER_DISTANCE_MEDIUM_NM) return "medium";
  if (nm < CHARTER_DISTANCE_LONG_NM) return "long";
  return "extreme";
}

export function buildMintAvoidance(input: {
  now?: Date;
  settings?: Partial<RecentRouteSettings> | null;
  declined?: InboxRouteMemoryEntry[];
  completed?: RouteLeg[];
  scheduled?: RouteLeg[];
  scenery?: SceneryAirport[];
}): MintAvoidance {
  const now = input.now ?? new Date();
  const settings = hydrateRecentRouteSettings(input.settings);
  const windowMs = settings.recentRouteCooldownDays * 24 * 60 * 60_000;
  const hardPairs = new Set<string>();
  const hardDests = new Set<string>();
  const softPairs = new Set<string>();
  const softDests = new Set<string>();
  const recentLongDests = new Set<string>();
  const recentRegions = new Set<string>();
  const recentNms: number[] = [];

  for (const row of input.declined ?? []) {
    if (!withinWindow(row.atUtc, now, windowMs)) continue;
    const dir = directionalFingerprint(row.originIcao, row.destIcao);
    const pair = pairFingerprint(row.originIcao, row.destIcao);
    hardPairs.add(dir);
    if (pair) hardPairs.add(pair);
    hardDests.add(normalizeIcao(row.destIcao));
    recentRegions.add(destRegionOf(row.destIcao));
    const nm = hopNm(row.originIcao, row.destIcao, input.scenery);
    if (nm != null) {
      recentNms.push(nm);
      if (nm >= CHARTER_DISTANCE_LONG_NM) recentLongDests.add(normalizeIcao(row.destIcao));
    }
  }

  if (settings.deprioritizeRecentRoutes) {
    for (const row of input.completed ?? []) {
      if (!withinWindow(row.atUtc, now, windowMs)) continue;
      const dir = directionalFingerprint(row.originIcao, row.destIcao);
      const pair = pairFingerprint(row.originIcao, row.destIcao);
      hardPairs.add(dir);
      if (pair) hardPairs.add(pair);
      hardDests.add(normalizeIcao(row.destIcao));
      recentRegions.add(destRegionOf(row.destIcao));
      const nm = hopNm(row.originIcao, row.destIcao, input.scenery);
      if (nm != null) {
        recentNms.push(nm);
        if (nm >= CHARTER_DISTANCE_LONG_NM) recentLongDests.add(normalizeIcao(row.destIcao));
      }
    }
  }

  for (const row of input.scheduled ?? []) {
    const dir = directionalFingerprint(row.originIcao, row.destIcao);
    const pair = pairFingerprint(row.originIcao, row.destIcao);
    softPairs.add(dir);
    if (pair) softPairs.add(pair);
    softDests.add(normalizeIcao(row.destIcao));
  }

  return {
    settings,
    hardPairs,
    hardDests,
    softPairs,
    softDests,
    recentLongDests,
    recentRegions,
    recentNms,
  };
}

export function mintPairIsHardAvoided(
  originIcao: string,
  destIcao: string,
  avoidance: MintAvoidance,
): boolean {
  const dir = directionalFingerprint(originIcao, destIcao);
  const pair = pairFingerprint(originIcao, destIcao);
  return avoidance.hardPairs.has(dir) || (Boolean(pair) && avoidance.hardPairs.has(pair));
}

export function mintDestIsHardAvoided(destIcao: string, avoidance: MintAvoidance): boolean {
  return avoidance.hardDests.has(normalizeIcao(destIcao));
}

/**
 * Lower is better (same spirit as destPreferenceRank).
 * Hard-avoided pairs score very high so sampling skips them when alternates exist.
 */
export function mintDestPenalty(
  originIcao: string,
  destIcao: string,
  avoidance: MintAvoidance,
  opts: { nm?: number | null; batchRegions?: ReadonlySet<string> } = {},
): number {
  let score = 0;
  const dest = normalizeIcao(destIcao);
  const region = destRegionOf(dest);
  if (mintPairIsHardAvoided(originIcao, destIcao, avoidance)) score += 80;
  else if (mintDestIsHardAvoided(dest, avoidance)) score += 55;
  if (avoidance.softPairs.has(directionalFingerprint(originIcao, destIcao))) score += 18;
  else if (avoidance.softDests.has(dest)) score += 10;
  if (avoidance.recentRegions.has(region)) score += 8;
  if (opts.batchRegions?.has(region)) score += 6;
  const nm = opts.nm;
  const band = distanceBand(nm);
  if (band === "extreme" && avoidance.recentLongDests.has(dest)) score += 24;
  else if (band === "extreme") score += 10;
  else if (band === "long" && avoidance.recentLongDests.size) score += 8;
  return score;
}

export function shouldExcludeMintPair(
  originIcao: string,
  destIcao: string,
  avoidance: MintAvoidance,
  alternateCount: number,
): boolean {
  if (alternateCount <= 0) return false;
  if (mintPairIsHardAvoided(originIcao, destIcao, avoidance)) return true;
  // Dest-wide cool-down is a hard skip only on a small allow-list. With many
  // Destinations ICAOs it is a penalty / prompt hint — do not collapse the set.
  if (
    mintDestIsHardAvoided(destIcao, avoidance) &&
    alternateCount >= 2 &&
    alternateCount < LARGE_ALLOWLIST_ICAO_FLOOR
  ) {
    return true;
  }
  return false;
}

export function requestMatchesMintAvoidance(
  row: Pick<CharterRequest, "originIcao" | "destIcao">,
  avoidance: MintAvoidance,
): boolean {
  return mintPairIsHardAvoided(row.originIcao, row.destIcao, avoidance);
}

/** Directional remint / cool-down pairs for the first Fetch prompt. */
export function hardAvoidPairLabels(avoidance: MintAvoidance, cap = 16): string[] {
  return [...avoidance.hardPairs].filter((k) => k.includes(">")).slice(0, cap);
}

export function hardAvoidDestLabels(avoidance: MintAvoidance, cap = 16): string[] {
  return [...avoidance.hardDests].slice(0, cap);
}

/**
 * Remaining Destinations after dest-wide cool-down. Idle's 48-cap sample
 * must still include legal dests — do not lead with only blocked ICAOs.
 */
export function usableAllowlistSample(
  scenery: ReadonlyArray<Pick<SceneryAirport, "icao">> | undefined,
  avoidance: MintAvoidance,
  cap = 16,
): string[] {
  const icaos: string[] = [];
  const seen = new Set<string>();
  for (const row of scenery ?? []) {
    const icao = normalizeIcao(row.icao);
    if (!icao || seen.has(icao)) continue;
    seen.add(icao);
    icaos.push(icao);
  }
  const unused = icaos.filter((icao) => !avoidance.hardDests.has(icao));
  const pool = unused.length >= 2 ? unused : icaos;
  return pool.slice(0, cap);
}

export function formatMintAvoidancePrompt(
  avoidance: MintAvoidance,
  scenery?: ReadonlyArray<Pick<SceneryAirport, "icao">>,
  bases?: readonly string[],
): string {
  const pairs = hardAvoidPairLabels(avoidance, 12);
  const dests = hardAvoidDestLabels(avoidance, 12);
  const scheduled = [...avoidance.softPairs].filter((k) => k.includes(">")).slice(0, 8);
  const sample = usableAllowlistSample(scenery, avoidance, 16);
  const lines = [
    "Mint memory (do not remint these as the next Fetch / trickle batch; never invent ICAOs):",
  ];
  if (pairs.length) {
    lines.push(
      `DO NOT USE these city-pairs (remint memory / recently declined, deleted, or completed): ${pairs.join(", ")}. Pick another allow-list pair. An empty inquiries list is not allowed.`,
    );
  }
  if (dests.length) {
    lines.push(
      `DO NOT USE these cool-down dests as the first pick: ${dests.join(", ")}. Prefer unused dests from the usable sample. On a large allow-list dest-wide cool-down is a hint — still emit legal inquiries.`,
    );
  }
  if (sample.length && (pairs.length || dests.length)) {
    lines.push(`Usable dest sample (prefer these; still only allow-list ICAOs): ${sample.join(", ")}.`);
  }
  if (scheduled.length) {
    lines.push(`SOFT avoid duplicates of already scheduled board tickets: ${scheduled.join(", ")}.`);
  }
  if (avoidance.recentLongDests.size) {
    lines.push(
      `Do not sticky-loop long-haul dests (${[...avoidance.recentLongDests].join(", ")}). Mix short/medium/long within company brief. Occasional long is OK — not MYNN/KBCT every Fetch.`,
    );
  }
  if (avoidance.settings.deprioritizeRecentRoutes) {
    lines.push(
      `Recently flown cool-down is ON (~${avoidance.settings.recentRouteCooldownDays} day${avoidance.settings.recentRouteCooldownDays === 1 ? "" : "s"} from Trip Log / completed tickets). Variety over same-region repeats.`,
    );
  }
  const live = (bases ?? []).map((icao) => normalizeIcao(icao)).filter((icao) => icao.length >= 3);
  const atlanticHome = live.some((icao) => destRegionOf(icao) === "maritimes" || icao === "CYPD");
  if (live.length && !atlanticHome) {
    lines.push(
      `Familiar dests follow current bases (${live.join(", ")}). Do not keep Port Hawkesbury / CYPD / Atlantic Canada just because older trips used them. Canada stays legal when the city-pair fits a current base.`,
    );
  }
  if (lines.length === 1) return "";
  return lines.join("\n");
}

export function mintAvoidanceFromDesk(input: {
  now?: Date;
  settings?: Partial<RecentRouteSettings> | null;
  inboxRouteMemory?: InboxRouteMemoryEntry[];
  flightLogs?: FlightLogEntry[];
  missions?: Mission[];
  scenery?: SceneryAirport[];
}): MintAvoidance {
  const settings = hydrateRecentRouteSettings(input.settings);
  const now = input.now ?? new Date();
  const windowMs = settings.recentRouteCooldownDays * 24 * 60 * 60_000;
  return buildMintAvoidance({
    now,
    settings,
    declined: input.inboxRouteMemory,
    completed: recentCompletedLegs({
      flightLogs: input.flightLogs,
      missions: input.missions,
      now,
      windowMs,
    }),
    scheduled: scheduledLegs(input.missions),
    scenery: input.scenery,
  });
}
